import { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
    BarChart3, 
    Truck, 
    Users, 
    Calendar,
    TrendingUp,
    Fuel,
    Wrench,
    Download,
    Loader2,
    Target,
    Activity,
    DollarSign,
    Filter
} from 'lucide-react';
import { 
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    LineChart, Line, PieChart, Pie, Cell, Legend
} from 'recharts';
import api from '../lib/api';
import { toast } from 'sonner';

const COLORS = ['#1B7EB5', '#58A6B4', '#8CC774', '#4ECDC4', '#FF6B6B', '#C44D58'];

// Safe number formatter with fallback
const safeNumber = (value, fallback = 0) => {
    const num = Number(value);
    return isNaN(num) ? fallback : num;
};

const formatNumber = (value) => {
    return safeNumber(value).toLocaleString();
};

const formatKm = (value) => {
    return safeNumber(value).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
};

export default function AnalyticsPage() {
    const { language } = useLanguage();
    const el = language === 'el';
    const [activeTab, setActiveTab] = useState('vehicles');
    const [vehicleData, setVehicleData] = useState(null);
    const [driverData, setDriverData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [days, setDays] = useState(365);
    const [selectedVehicle, setSelectedVehicle] = useState('all');

    useEffect(() => {
        fetchAnalytics();
    }, [days]);

    const fetchAnalytics = async () => {
        setLoading(true);
        try {
            const [vehicleRes, driverRes] = await Promise.all([
                api.get(`/analytics/vehicles?days=${days}`),
                api.get(`/analytics/drivers?days=${days}`)
            ]);
            setVehicleData(vehicleRes.data);
            setDriverData(driverRes.data);
        } catch (error) {
            toast.error(el ? 'Αποτυχία φόρτωσης στατιστικών' : 'Failed to fetch analytics data');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    // Filter vehicles for charts
    const getFilteredVehicles = () => {
        if (!vehicleData?.vehicles) return [];
        if (selectedVehicle === 'all') return vehicleData.vehicles.slice(0, 8);
        return vehicleData.vehicles.filter(v => v.vehicle_id === selectedVehicle);
    };

    // Get selected vehicle stats for single vehicle view
    const getSelectedVehicleStats = () => {
        if (!vehicleData?.vehicles || selectedVehicle === 'all') return null;
        return vehicleData.vehicles.find(v => v.vehicle_id === selectedVehicle);
    };

    const exportVehicleCSV = () => {
        if (!vehicleData?.vehicles) return;
        
        const headers = [
            ...(el ? ['Όχημα', 'Κατάσταση', 'Χιλιόμετρα (περίοδος)', 'Ημέρες χρήσης', 'Λίτρα καυσίμου',
                'Συνολικό κόστος καυσίμων', 'Κόστος συντήρησης', 'Πλήθος συντηρήσεων', 'Επιθεωρήσεις', 'Αξιοποίηση (%)', 'Απόδοση καυσίμου (km/L)']
                : ['Vehicle', 'Status', 'KM Driven (Period)', 'Days Used', 'Fuel Liters',
                'Total Fuel Cost', 'Maintenance Cost', 'Maintenance Count', 'Inspections', 'Utilization (%)', 'Fuel Efficiency (km/L)'])
        ];
        
        const rows = vehicleData.vehicles.map(v => [
            v.vehicle_info,
            v.status,
            safeNumber(v.distance_km),
            safeNumber(v.days_used),
            safeNumber(v.total_fuel_liters),
            safeNumber(v.total_fuel_cost),
            safeNumber(v.maintenance_cost),
            safeNumber(v.maintenance_count),
            safeNumber(v.inspections),
            safeNumber(v.utilization_percentage),
            safeNumber(v.fuel_efficiency_km_l)
        ]);
        
        downloadCSV(headers, rows, `vehicle_analytics_${days}days.csv`);
        toast.success(el ? 'Τα στατιστικά οχημάτων εξήχθησαν' : 'Vehicle analytics exported');
    };

    const exportDriverCSV = () => {
        if (!driverData?.drivers) return;
        
        const headers = [
            ...(el ? ['Οδηγός', 'Συνολικά χιλιόμετρα', 'Μ.Ο. km/παραλαβή', 'Οχήματα', 'Παραλαβές', 'Ενεργές ημέρες', 'Συμμόρφωση (%)']
                : ['Driver Name', 'Total KM Driven', 'Avg KM/Check-in', 'Vehicles Used', 'Check-in Count', 'Active Days', 'Compliance Rate (%)'])
        ];
        
        const rows = driverData.drivers.map(d => [
            d.driver_name,
            d.total_km_driven,
            d.avg_km_per_checkin,
            d.vehicles_used,
            d.checkin_count,
            d.unique_active_days,
            d.compliance_rate
        ]);
        
        downloadCSV(headers, rows, `driver_analytics_${days}days.csv`);
        toast.success(el ? 'Τα στατιστικά οδηγών εξήχθησαν' : 'Driver analytics exported');
    };

    const downloadCSV = (headers, rows, filename) => {
        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 text-[#1B7EB5] animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="analytics-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800 font-heading flex items-center gap-2">
                        <BarChart3 className="w-6 h-6 text-[#1B7EB5]" />
                        {el ? 'Στατιστικά Στόλου' : 'Fleet Analytics'}
                    </h1>
                    <p className="text-slate-500 mt-1">
                        {el ? 'Συνολική εικόνα της απόδοσης του στόλου σας' : 'Comprehensive insights into your fleet performance'}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <select
                        value={days}
                        onChange={(e) => setDays(parseInt(e.target.value))}
                        className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700"
                        data-testid="analytics-period-select"
                    >
                        <option value={30}>{el ? 'Τελευταίες 30 ημέρες' : 'Last 30 days'}</option>
                        <option value={90}>{el ? 'Τελευταίοι 3 μήνες' : 'Last 90 days'}</option>
                        <option value={180}>{el ? 'Τελευταίοι 6 μήνες' : 'Last 6 months'}</option>
                        <option value={365}>{el ? 'Τελευταίος χρόνος' : 'Last year'}</option>
                    </select>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 border-b border-slate-200 pb-2">
                <Button
                    variant={activeTab === 'vehicles' ? 'default' : 'ghost'}
                    onClick={() => { setActiveTab('vehicles'); setSelectedVehicle('all'); }}
                    className={activeTab === 'vehicles' ? 'bg-[#1B7EB5] text-white' : 'text-slate-500'}
                    data-testid="tab-vehicles"
                >
                    <Truck className="w-4 h-4 mr-2" />
                    {el ? 'Στατιστικά Οχημάτων' : 'Vehicle Analytics'}
                </Button>
                <Button
                    variant={activeTab === 'drivers' ? 'default' : 'ghost'}
                    onClick={() => setActiveTab('drivers')}
                    className={activeTab === 'drivers' ? 'bg-[#1B7EB5] text-white' : 'text-slate-500'}
                    data-testid="tab-drivers"
                >
                    <Users className="w-4 h-4 mr-2" />
                    {el ? 'Στατιστικά Οδηγών' : 'Driver Analytics'}
                </Button>
            </div>

            {/* Vehicle Analytics Tab */}
            {activeTab === 'vehicles' && vehicleData && (
                <div className="space-y-6">
                    {/* Vehicle Filter */}
                    <div className="flex items-center gap-4 p-4 bg-white border border-slate-200 rounded-xl shadow-sm">
                        <Filter className="w-5 h-5 text-slate-400" />
                        <span className="text-sm text-slate-600">{el ? 'Φίλτρο οχήματος:' : 'Filter by Vehicle:'}</span>
                        <Select value={selectedVehicle} onValueChange={setSelectedVehicle}>
                            <SelectTrigger className="w-[250px] bg-white border-slate-200" data-testid="vehicle-filter-select">
                                <SelectValue placeholder={el ? 'Επιλέξτε όχημα' : 'Select a vehicle'} />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-slate-200">
                                <SelectItem value="all">{el ? 'Όλα τα οχήματα' : 'All Vehicles'}</SelectItem>
                                {vehicleData.vehicles.map(vehicle => (
                                    <SelectItem key={vehicle.vehicle_id} value={vehicle.vehicle_id}>
                                        {vehicle.make} {vehicle.model} ({vehicle.license_plate})
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {selectedVehicle !== 'all' && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setSelectedVehicle('all')}
                                className="text-slate-500 hover:text-slate-700"
                            >
                                {el ? 'Καθαρισμός φίλτρου' : 'Clear Filter'}
                            </Button>
                        )}
                    </div>

                    {/* Single Vehicle Stats (when filtered) */}
                    {selectedVehicle !== 'all' && getSelectedVehicleStats() && (
                        <div className="bg-gradient-to-r from-[#1B7EB5]/10 to-[#8CC774]/10 border border-[#1B7EB5]/20 rounded-xl p-6">
                            <div className="flex items-center gap-4 mb-4">
                                <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center shadow-sm">
                                    <Truck className="w-8 h-8 text-[#1B7EB5]" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-slate-800">
                                        {getSelectedVehicleStats().make} {getSelectedVehicleStats().model}
                                    </h3>
                                    <p className="text-[#1B7EB5] font-medium">{getSelectedVehicleStats().license_plate}</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                                <div className="bg-white/80 rounded-lg p-3">
                                    <p className="text-xs text-slate-500 uppercase">{el ? 'Χιλιόμετρα' : 'KM Driven'}</p>
                                    <p className="text-2xl font-bold text-[#1B7EB5]">{formatNumber(getSelectedVehicleStats()?.distance_km || getSelectedVehicleStats()?.km_driven_period)}</p>
                                </div>
                                <div className="bg-white/80 rounded-lg p-3">
                                    <p className="text-xs text-slate-500 uppercase">{el ? 'Μέσος όρος ΧΛΜ/ημέρα' : 'Avg KM/Day'}</p>
                                    <p className="text-2xl font-bold text-[#58A6B4]">{formatNumber(getSelectedVehicleStats()?.avg_km_per_day || (safeNumber(getSelectedVehicleStats()?.distance_km) / Math.max(getSelectedVehicleStats()?.days_used || 1, 1)))}</p>
                                </div>
                                <div className="bg-white/80 rounded-lg p-3">
                                    <p className="text-xs text-slate-500 uppercase">{el ? 'Απόδοση καυσίμου' : 'Fuel Efficiency'}</p>
                                    <p className="text-2xl font-bold text-[#8CC774]">{formatNumber(getSelectedVehicleStats()?.fuel_efficiency_km_l)} km/L</p>
                                </div>
                                <div className="bg-white/80 rounded-lg p-3">
                                    <p className="text-xs text-slate-500 uppercase">{el ? 'Κόστος καυσίμων' : 'Fuel Cost'}</p>
                                    <p className="text-2xl font-bold text-[#8CC774]">€{formatNumber(safeNumber(getSelectedVehicleStats()?.total_fuel_cost))}</p>
                                </div>
                                <div className="bg-white/80 rounded-lg p-3">
                                    <p className="text-xs text-slate-500 uppercase">{el ? 'Κόστος συντήρησης' : 'Maintenance Cost'}</p>
                                    <p className="text-2xl font-bold text-[#FF6B6B]">€{formatNumber(safeNumber(getSelectedVehicleStats()?.maintenance_cost))}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Fleet Summary Cards (when not filtered) */}
                    {selectedVehicle === 'all' && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="kpi-card">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="w-10 h-10 bg-[#1B7EB5]/10 rounded-lg flex items-center justify-center">
                                    <TrendingUp className="w-5 h-5 text-[#1B7EB5]" />
                                </div>
                            </div>
                            <p className="kpi-label">{el ? 'Συνολικά χιλιόμετρα στόλου' : 'Total Fleet Odometer'}</p>
                            <p className="kpi-value">{formatKm(vehicleData.fleet_summary?.total_odometer_km)}</p>
                            <p className="text-xs text-slate-400 mt-1">
                                {el
                                    ? `Άθροισμα οδομέτρου ${vehicleData.vehicle_count || 0} οχημάτων`
                                    : `Sum of odometer across ${vehicleData.vehicle_count || 0} vehicles`}
                            </p>
                        </div>
                        <div className="kpi-card">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="w-10 h-10 bg-[#58A6B4]/10 rounded-lg flex items-center justify-center">
                                    <Target className="w-5 h-5 text-[#58A6B4]" />
                                </div>
                            </div>
                            <p className="kpi-label">{el ? 'Μέσος όρος ΧΛΜ/όχημα' : 'Avg KM/Vehicle'}</p>
                            <p className="kpi-value">{formatKm(vehicleData.fleet_summary?.avg_km_per_vehicle)}</p>
                            <p className="text-xs text-slate-400 mt-1">
                                {el
                                    ? `Αφορά ${vehicleData.fleet_summary?.active_vehicle_count || 0} από ${vehicleData.vehicle_count || 0} οχήματα με κίνηση στην περίοδο`
                                    : `Based on ${vehicleData.fleet_summary?.active_vehicle_count || 0} of ${vehicleData.vehicle_count || 0} vehicles active in period`}
                            </p>
                        </div>
                        <div className="kpi-card">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="w-10 h-10 bg-[#8CC774]/10 rounded-lg flex items-center justify-center">
                                    <Fuel className="w-5 h-5 text-[#8CC774]" />
                                </div>
                            </div>
                            <p className="kpi-label">{el ? 'Συνολικό κόστος καυσίμων' : 'Total Fuel Cost'}</p>
                            <p className="kpi-value">€{formatNumber(safeNumber(vehicleData.fleet_summary?.total_fuel_cost))}</p>
                        </div>
                        <div className="kpi-card">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="w-10 h-10 bg-[#FF6B6B]/10 rounded-lg flex items-center justify-center">
                                    <Wrench className="w-5 h-5 text-[#FF6B6B]" />
                                </div>
                            </div>
                            <p className="kpi-label">{el ? 'Κόστος συντήρησης' : 'Maintenance Cost'}</p>
                            <p className="kpi-value">€{formatNumber(safeNumber(vehicleData.fleet_summary?.total_maintenance_cost))}</p>
                        </div>
                    </div>
                    )}

                    {/* Charts Row */}
                    <div className="grid md:grid-cols-1 gap-6">
                        {/* Combined KM Driven + Fuel Efficiency Chart */}
                        <div className="fleet-card p-4" data-testid="km-combined-chart">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
                                <h3 className="text-lg font-semibold text-slate-800">
                                    {el ? 'Χιλιόμετρα & απόδοση καυσίμου ανά όχημα' : 'KM Driven by Vehicle & Fuel Efficiency'}
                                </h3>
                            </div>
                            <ResponsiveContainer width="100%" height={350}>
                                <BarChart data={getFilteredVehicles().map(v => ({...v, license_plate: v.vehicle_info?.split('(')[1]?.replace(')', '') || v.license_plate}))} barGap={2} barCategoryGap="15%">
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                    <XAxis dataKey="license_plate" tick={{ fill: '#64748b', fontSize: 11 }} />
                                    <YAxis yAxisId="km" tick={{ fill: '#64748b' }} tickFormatter={(value) => formatNumber(value)} />
                                    <YAxis yAxisId="eff" orientation="right" tick={{ fill: '#8CC774' }} tickFormatter={(value) => formatNumber(value)} />
                                    <Tooltip 
                                        contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                                        labelStyle={{ color: '#334155', fontWeight: 'bold' }}
                                        formatter={(value, name, item) => [formatNumber(value) + (item.dataKey === 'fuel_efficiency_km_l' ? ' km/L' : ' km'), name]}
                                    />
                                    <Legend 
                                        verticalAlign="top" 
                                        height={36}
                                        wrapperStyle={{ paddingBottom: '10px' }}
                                        formatter={(value) => <span style={{ color: '#334155', fontSize: '13px' }}>{value}</span>}
                                    />
                                    <Bar 
                                        dataKey="distance_km" yAxisId="km" 
                                        fill="#1B7EB5" 
                                        name={el ? 'Χιλιόμετρα (περίοδος)' : 'KM Driven (Period)'} 
                                        radius={[4, 4, 0, 0]} 
                                    />
                                    <Bar 
                                        dataKey="fuel_efficiency_km_l" yAxisId="eff" 
                                        fill="#8CC774" 
                                        name={el ? 'Απόδοση καυσίμου (km/L)' : 'Fuel Efficiency (km/L)'} 
                                        radius={[4, 4, 0, 0]} 
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                            {/* Legend Explanation */}
                            <div className="mt-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                                <p className="text-sm text-slate-600">
                                    <span className="inline-block w-3 h-3 bg-[#1B7EB5] rounded mr-2"></span>
                                    <strong>{el ? 'Χιλιόμετρα (περίοδος):' : 'KM Driven (Period):'}</strong> {el ? 'Συνολικά χιλιόμετρα που διανύθηκαν στην επιλεγμένη περίοδο.' : 'Total kilometers traveled during the selected period.'}
                                </p>
                                <p className="text-sm text-slate-600 mt-1">
                                    <span className="inline-block w-3 h-3 bg-[#8CC774] rounded mr-2"></span>
                                    <strong>{el ? 'Απόδοση καυσίμου (km/L):' : 'Fuel Efficiency (km/L):'}</strong> {el ? 'Χιλιόμετρα ανά λίτρο καυσίμου για κάθε όχημα.' : 'Kilometers per liter of fuel for each vehicle.'}
                                </p>
                            </div>
                        </div>

                        {/* Fuel Logs by Vehicle Chart */}
                        <div className="fleet-card p-4" data-testid="fuel-logs-chart">
                            <h3 className="text-lg font-semibold text-slate-800 mb-4">
                                {el ? 'Καταγραφές καυσίμων ανά όχημα' : 'Fuel Logs by Vehicle'}
                            </h3>
                            <ResponsiveContainer width="100%" height={350}>
                                <BarChart data={getFilteredVehicles().map(v => ({...v, license_plate: v.vehicle_info?.split('(')[1]?.replace(')', '') || v.license_plate}))} barGap={2} barCategoryGap="15%">
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                    <XAxis dataKey="license_plate" tick={{ fill: '#64748b', fontSize: 11 }} />
                                    <YAxis 
                                        tick={{ fill: '#64748b' }} 
                                        tickFormatter={(value) => formatNumber(value)}
                                    />
                                    <Tooltip 
                                        contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                                        labelStyle={{ color: '#334155', fontWeight: 'bold' }}
                                        formatter={(value, name, item) => {
                                            if (item.dataKey === 'total_fuel_liters') return [formatNumber(value) + ' L', name];
                                            if (item.dataKey === 'total_fuel_cost') return ['€' + formatNumber(value), name];
                                            return [value, name];
                                        }}
                                    />
                                    <Legend 
                                        verticalAlign="top" 
                                        height={36}
                                        wrapperStyle={{ paddingBottom: '10px' }}
                                        formatter={(value) => <span style={{ color: '#334155', fontSize: '13px' }}>{value}</span>}
                                    />
                                    <Bar 
                                        dataKey="total_fuel_liters" 
                                        fill="#58A6B4" 
                                        name={el ? 'Καύσιμα (λίτρα)' : 'Fuel (Liters)'} 
                                        radius={[4, 4, 0, 0]} 
                                    />
                                    <Bar 
                                        dataKey="total_fuel_cost" 
                                        fill="#FF6B6B" 
                                        name={el ? 'Κόστος καυσίμων (€)' : 'Fuel Cost (€)'} 
                                        radius={[4, 4, 0, 0]} 
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                            {/* Legend Explanation */}
                            <div className="mt-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                                <p className="text-sm text-slate-600">
                                    <span className="inline-block w-3 h-3 bg-[#58A6B4] rounded mr-2"></span>
                                    <strong>{el ? 'Καύσιμα (λίτρα):' : 'Fuel (Liters):'}</strong> {el ? 'Συνολική κατανάλωση καυσίμου κάθε οχήματος στην επιλεγμένη περίοδο.' : 'Total fuel consumed by each vehicle during the selected period.'}
                                </p>
                                <p className="text-sm text-slate-600 mt-1">
                                    <span className="inline-block w-3 h-3 bg-[#FF6B6B] rounded mr-2"></span>
                                    <strong>{el ? 'Κόστος καυσίμων (€):' : 'Fuel Cost (€):'}</strong> {el ? 'Συνολικό ποσό που δαπανήθηκε σε καύσιμα για κάθε όχημα.' : 'Total amount spent on fuel for each vehicle.'}
                                </p>
                            </div>
                        </div>

                        {/* Maintenance by Vehicle Chart */}
                        <div className="fleet-card p-4" data-testid="maintenance-chart">
                            <h3 className="text-lg font-semibold text-slate-800 mb-4">
                                {el ? 'Συντηρήσεις ανά όχημα' : 'Maintenance by Vehicle'}
                            </h3>
                            <ResponsiveContainer width="100%" height={350}>
                                <BarChart data={getFilteredVehicles().map(v => ({...v, license_plate: v.vehicle_info?.split('(')[1]?.replace(')', '') || v.license_plate}))} barGap={2} barCategoryGap="15%">
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                    <XAxis dataKey="license_plate" tick={{ fill: '#64748b', fontSize: 11 }} />
                                    <YAxis 
                                        tick={{ fill: '#64748b' }} 
                                        tickFormatter={(value) => formatNumber(value)}
                                    />
                                    <Tooltip 
                                        contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                                        labelStyle={{ color: '#334155', fontWeight: 'bold' }}
                                        formatter={(value, name, item) => {
                                            if (item.dataKey === 'maintenance_count') return [formatNumber(value) + (el ? (Number(value) === 1 ? ' συντήρηση' : ' συντηρήσεις') : (Number(value) === 1 ? ' service' : ' services')), name];
                                            if (item.dataKey === 'maintenance_cost') return ['€' + formatNumber(value), name];
                                            return [value, name];
                                        }}
                                    />
                                    <Legend 
                                        verticalAlign="top" 
                                        height={36}
                                        wrapperStyle={{ paddingBottom: '10px' }}
                                        formatter={(value) => <span style={{ color: '#334155', fontSize: '13px' }}>{value}</span>}
                                    />
                                    <Bar 
                                        dataKey="maintenance_count" 
                                        fill="#9333EA" 
                                        name={el ? 'Πλήθος συντηρήσεων' : 'Maintenance Count'} 
                                        radius={[4, 4, 0, 0]} 
                                    />
                                    <Bar 
                                        dataKey="maintenance_cost" 
                                        fill="#F59E0B" 
                                        name={el ? 'Κόστος συντήρησης (€)' : 'Maintenance Cost (€)'} 
                                        radius={[4, 4, 0, 0]} 
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                            {/* Legend Explanation */}
                            <div className="mt-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                                <p className="text-sm text-slate-600">
                                    <span className="inline-block w-3 h-3 bg-[#9333EA] rounded mr-2"></span>
                                    <strong>{el ? 'Πλήθος συντηρήσεων:' : 'Maintenance Count:'}</strong> {el ? 'Αριθμός συντηρήσεων που έγιναν σε κάθε όχημα.' : 'Number of maintenance services performed on each vehicle.'}
                                </p>
                                <p className="text-sm text-slate-600 mt-1">
                                    <span className="inline-block w-3 h-3 bg-[#F59E0B] rounded mr-2"></span>
                                    <strong>{el ? 'Κόστος συντήρησης (€):' : 'Maintenance Cost (€):'}</strong> {el ? 'Συνολικό κόστος συντήρησης για κάθε όχημα.' : 'Total cost of maintenance for each vehicle.'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Vehicle Table */}
                    <div className="fleet-card overflow-hidden">
                        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-slate-800">
                                {selectedVehicle === 'all' ? (el ? 'Λεπτομέρειες οχημάτων' : 'Vehicle Details') : (el ? 'Λεπτομέρειες επιλεγμένου οχήματος' : 'Selected Vehicle Details')}
                            </h3>
                            <Button
                                onClick={exportVehicleCSV}
                                variant="outline"
                                size="sm"
                                className="border-[#1B7EB5] text-[#1B7EB5] hover:bg-[#1B7EB5]/10"
                                data-testid="export-vehicle-csv"
                            >
                                <Download className="w-4 h-4 mr-2" />
                                {el ? 'Εξαγωγή CSV' : 'Export CSV'}
                            </Button>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>{el ? 'Όχημα' : 'Vehicle'}</th>
                                        <th>{el ? 'Κατάσταση' : 'Status'}</th>
                                        <th>{el ? 'Χιλιόμετρα' : 'KM Driven'}</th>
                                        <th>{el ? 'Μ.Ο. km/ημέρα' : 'Avg KM/Day'}</th>
                                        <th>{el ? 'Απόδοση καυσίμου' : 'Fuel Efficiency'}</th>
                                        <th>{el ? 'Κόστος καυσίμων' : 'Fuel Cost'}</th>
                                        <th>{el ? 'Κόστος συντ.' : 'Maint. Cost'}</th>
                                        <th>{el ? 'Κόστος/km' : 'Cost/KM'}</th>
                                        <th>{el ? 'Αξιοποίηση' : 'Utilization'}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(selectedVehicle === 'all' ? vehicleData.vehicles : getFilteredVehicles()).map((vehicle) => (
                                        <tr key={vehicle.vehicle_id} className="table-row-hover">
                                            <td>
                                                <div>
                                                    <p className="font-medium text-slate-800">{vehicle.vehicle_info || `${vehicle.make} ${vehicle.model}`}</p>
                                                    <p className="text-xs text-[#1B7EB5]">{vehicle.license_plate}</p>
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`badge ${vehicle.status === 'active' ? 'badge-success' : 'badge-warning'}`}>
                                                    {el ? ({ active: 'Ενεργό', maintenance: 'Συντήρηση', inactive: 'Ανενεργό' }[vehicle.status] || vehicle.status) : vehicle.status}
                                                </span>
                                            </td>
                                            <td className="text-slate-800 font-medium">{formatNumber(vehicle.distance_km || vehicle.km_driven_period)} km</td>
                                            <td>{formatNumber(vehicle.avg_km_per_day || (safeNumber(vehicle.distance_km) / Math.max(vehicle.days_used || 1, 1)))} km</td>
                                            <td className="text-[#8CC774] font-medium">{formatNumber(vehicle.fuel_efficiency_km_l)} km/L</td>
                                            <td>€{formatNumber(vehicle.total_fuel_cost)}</td>
                                            <td>€{formatNumber(vehicle.maintenance_cost || vehicle.total_maintenance_cost)}</td>
                                            <td className="text-[#58A6B4]">€{formatNumber(vehicle.cost_per_km || (safeNumber(vehicle.total_cost) / Math.max(safeNumber(vehicle.distance_km), 1)))}</td>
                                            <td>
                                                <div className="flex items-center gap-2">
                                                    <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                                                        <div 
                                                            className="h-full bg-[#1B7EB5]" 
                                                            style={{ width: `${Math.min(safeNumber(vehicle.utilization_percentage || vehicle.utilization_rate), 100)}%` }}
                                                        />
                                                    </div>
                                                    <span className="text-xs">{formatNumber(vehicle.utilization_percentage || vehicle.utilization_rate)}%</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Driver Analytics Tab */}
            {activeTab === 'drivers' && driverData && (
                <div className="space-y-6">
                    {/* Driver Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="kpi-card">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="w-10 h-10 bg-[#1B7EB5]/10 rounded-lg flex items-center justify-center">
                                    <Users className="w-5 h-5 text-[#1B7EB5]" />
                                </div>
                            </div>
                            <p className="kpi-label">{el ? 'Σύνολο οδηγών' : 'Total Drivers'}</p>
                            <p className="kpi-value">{formatNumber(driverData.total_drivers)}</p>
                        </div>
                        <div className="kpi-card">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="w-10 h-10 bg-[#58A6B4]/10 rounded-lg flex items-center justify-center">
                                    <TrendingUp className="w-5 h-5 text-[#58A6B4]" />
                                </div>
                            </div>
                            <p className="kpi-label">{el ? 'Συνολικά χιλιόμετρα' : 'Total KM Driven'}</p>
                            <p className="kpi-value">{formatNumber(driverData.fleet_summary?.total_km_driven || driverData.fleet_summary?.total_distance_km)}</p>
                        </div>
                        <div className="kpi-card">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="w-10 h-10 bg-[#8CC774]/10 rounded-lg flex items-center justify-center">
                                    <Target className="w-5 h-5 text-[#8CC774]" />
                                </div>
                            </div>
                            <p className="kpi-label">{el ? 'Μέσος όρος ΧΛΜ/οδηγό' : 'Avg KM/Driver'}</p>
                            <p className="kpi-value">{formatNumber(driverData.fleet_summary?.avg_km_per_driver)}</p>
                        </div>
                        <div className="kpi-card">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="w-10 h-10 bg-[#4ECDC4]/10 rounded-lg flex items-center justify-center">
                                    <Activity className="w-5 h-5 text-[#4ECDC4]" />
                                </div>
                            </div>
                            <p className="kpi-label">{el ? 'Συνολικές παραλαβές' : 'Total Check-ins'}</p>
                            <p className="kpi-value">{formatNumber(driverData.fleet_summary?.total_checkins)}</p>
                        </div>
                    </div>

                    {/* Charts Row */}
                    <div className="grid md:grid-cols-2 gap-6">
                        {/* KM by Driver Chart */}
                        <div className="fleet-card p-4">
                            <h3 className="text-lg font-semibold text-slate-800 mb-4">{el ? 'Χιλιόμετρα ανά οδηγό' : 'KM Driven by Driver'}</h3>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={driverData.drivers.slice(0, 8)} layout="vertical">
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                    <XAxis type="number" tick={{ fill: '#64748b' }} />
                                    <YAxis dataKey="driver_name" type="category" tick={{ fill: '#64748b', fontSize: 11 }} width={100} />
                                    <Tooltip 
                                        contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                                        labelStyle={{ color: '#334155' }}
                                    />
                                    <Bar dataKey="total_km_driven" fill="#58A6B4" name={el ? 'Χιλιόμετρα' : 'KM Driven'} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Compliance Rate Chart */}
                        <div className="fleet-card p-4">
                            <h3 className="text-lg font-semibold text-slate-800 mb-4">{el ? 'Ποσοστό συμμόρφωσης παραλαβών' : 'Check-in Compliance Rate'}</h3>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={driverData.drivers.slice(0, 8)} layout="vertical">
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                    <XAxis type="number" domain={[0, 100]} tick={{ fill: '#64748b' }} />
                                    <YAxis dataKey="driver_name" type="category" tick={{ fill: '#64748b', fontSize: 11 }} width={100} />
                                    <Tooltip 
                                        contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                                        labelStyle={{ color: '#334155' }}
                                        formatter={(value) => [`${value}%`, el ? 'Συμμόρφωση' : 'Compliance']}
                                    />
                                    <Bar dataKey="compliance_rate" fill="#8CC774" name={el ? 'Συμμόρφωση %' : 'Compliance %'} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Weekly Activity Pattern (First Driver) */}
                    {driverData.drivers[0]?.activity_by_day && (
                        <div className="fleet-card p-4">
                            <h3 className="text-lg font-semibold text-slate-800 mb-4">{el ? 'Εβδομαδιαία δραστηριότητα (μέσος όρος στόλου)' : 'Weekly Activity Pattern (Fleet Average)'}</h3>
                            <ResponsiveContainer width="100%" height={200}>
                                <BarChart data={driverData.drivers[0].activity_by_day}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                    <XAxis dataKey="day" tick={{ fill: '#64748b' }} />
                                    <YAxis tick={{ fill: '#64748b' }} />
                                    <Tooltip 
                                        contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                                        labelStyle={{ color: '#334155' }}
                                    />
                                    <Bar dataKey="checkins" fill="#1B7EB5" name={el ? 'Παραλαβές' : 'Check-ins'} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}

                    {/* Driver Table */}
                    <div className="fleet-card overflow-hidden">
                        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-slate-800">{el ? 'Λεπτομέρειες οδηγών' : 'Driver Details'}</h3>
                            <Button
                                onClick={exportDriverCSV}
                                variant="outline"
                                size="sm"
                                className="border-[#1B7EB5] text-[#1B7EB5] hover:bg-[#1B7EB5]/10"
                                data-testid="export-driver-csv"
                            >
                                <Download className="w-4 h-4 mr-2" />
                                {el ? 'Εξαγωγή CSV' : 'Export CSV'}
                            </Button>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>{el ? 'Οδηγός' : 'Driver'}</th>
                                        <th>{el ? 'Συνολικά km' : 'Total KM'}</th>
                                        <th>{el ? 'Μ.Ο. km/παραλαβή' : 'Avg KM/Check-in'}</th>
                                        <th>{el ? 'Οχήματα' : 'Vehicles Used'}</th>
                                        <th>{el ? 'Παραλαβές' : 'Check-ins'}</th>
                                        <th>{el ? 'Ενεργές ημέρες' : 'Active Days'}</th>
                                        <th>{el ? 'Συμμόρφωση' : 'Compliance'}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {driverData.drivers.map((driver) => (
                                        <tr key={driver.driver_id} className="table-row-hover">
                                            <td>
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 bg-[#1B7EB5]/20 rounded-full flex items-center justify-center text-[#1B7EB5] font-bold text-sm">
                                                        {(driver.driver_name || 'U').charAt(0)}
                                                    </div>
                                                    <span className="font-medium text-slate-800">{driver.driver_name || (el ? 'Άγνωστος' : 'Unknown')}</span>
                                                </div>
                                            </td>
                                            <td className="text-slate-800 font-medium">{formatNumber(driver.total_km_driven)} km</td>
                                            <td>{formatNumber(driver.avg_km_per_checkin)} km</td>
                                            <td>
                                                <span className="text-[#58A6B4]">{formatNumber(driver.vehicles_used)}</span>
                                                {driver.vehicle_breakdown?.length > 0 && (
                                                    <div className="text-xs text-slate-500 mt-1">
                                                        {el ? 'Κύριο' : 'Top'}: {driver.vehicle_breakdown[0]?.vehicle_info?.split('(')[0]}
                                                    </div>
                                                )}
                                            </td>
                                            <td>{formatNumber(driver.checkin_count)}</td>
                                            <td>{formatNumber(driver.unique_active_days)}</td>
                                            <td>
                                                <div className="flex items-center gap-2">
                                                    <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                                                        <div 
                                                            className={`h-full ${driver.compliance_rate >= 80 ? 'bg-[#8CC774]' : driver.compliance_rate >= 50 ? 'bg-[#58A6B4]' : 'bg-[#FF6B6B]'}`}
                                                            style={{ width: `${Math.min(driver.compliance_rate, 100)}%` }}
                                                        />
                                                    </div>
                                                    <span className="text-xs">{driver.compliance_rate}%</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
